import React, { useContext } from 'react'
import { LocalizationContext } from '../../shared/contexts'
import { formatTenantDateTime } from '../../../utils/formatTenantDateTime'
import moment from 'moment-timezone'
import { Box, Typography } from '@mui/material'
import { NA } from './Timeline'
import { voucherReasonLabel, voucherReasonDetail, invalidatedReasonLabel, VoucherLabelSource } from '../../shared/voucherLabels'
import { TIME_SOURCE, timeSourceTitle } from '../../../utils/timeSource'

export interface VoucherSmsEvent {
  id: number
  status: string
  sent_at: string
  error_message: string | null
}

export interface VoucherLifecycle extends VoucherLabelSource {
  token: string
  status: string
  note: string | null
  created_at: string | null
  expires_at: string | null
  redeemed_at: string | null
  redeemed_order_id: number | null
  redeemed_order_number: string | null
  invalidated_at: string | null
  invalidated_reason: string | null
  invalidated_by: string | null
  invalidation_note: string | null
  sms_events: VoucherSmsEvent[]
}

interface LifecycleEvent {
  label: string
  at: string | null
  detail: React.ReactNode
  pending?: boolean
}

const SMS_DETAIL_OVERRIDES: Record<string, string> = {
  'Admin user, skipping SMS delivery': 'admin user — no SMS sent'
}

const smsDetail = (sms: VoucherSmsEvent): string => {
  if (!sms.error_message) return sms.status

  return `${sms.status} — ${SMS_DETAIL_OVERRIDES[sms.error_message] || sms.error_message}`
}

const lifecycleEvents = (voucher: VoucherLifecycle): LifecycleEvent[] => {
  const issuedDetail = [voucherReasonLabel(voucher), voucherReasonDetail(voucher), voucher.note]
    .filter(Boolean)
    .join(' — ')

  const events: LifecycleEvent[] = [
    { label: 'Issued', at: voucher.created_at, detail: issuedDetail }
  ]

  voucher.sms_events.forEach((sms) => {
    events.push({ label: 'Voucher issued SMS', at: sms.sent_at, detail: smsDetail(sms) })
  })

  if (voucher.redeemed_at) {
    events.push({
      label: 'Redeemed',
      at: voucher.redeemed_at,
      detail: voucher.redeemed_order_number && (
        <>
          spent on order{' '}
          {voucher.redeemed_order_id
            ? <a href={`/orders/${voucher.redeemed_order_id}`}>{voucher.redeemed_order_number}</a>
            : voucher.redeemed_order_number}
        </>
      )
    })
  }

  if (voucher.invalidated_at) {
    const by = voucher.invalidated_by ? ` — ${voucher.invalidated_by}` : ''
    const note = voucher.invalidation_note ? ` — ${voucher.invalidation_note}` : ''
    events.push({
      label: 'Invalidated',
      at: voucher.invalidated_at,
      detail: `${invalidatedReasonLabel(voucher.invalidated_reason)}${by}${note}`
    })
  }

  events.sort((a, b) => moment(a.at || 0).valueOf() - moment(b.at || 0).valueOf())

  if (voucher.status === 'expired') {
    events.push({ label: 'Expired unused', at: voucher.expires_at, detail: null })
  } else if (voucher.status === 'active') {
    events.push({
      label: 'Expires',
      at: voucher.expires_at,
      detail: voucher.expires_at ? moment(voucher.expires_at).fromNow() : null,
      pending: true
    })
  }

  return events
}

const VoucherTimeline = ({
  voucher,
  header
}: { voucher: VoucherLifecycle; header?: React.ReactNode }) => {
  const localization = useContext(LocalizationContext)
  const formatDateTime = (value: string | null) => formatTenantDateTime(value, localization)

  return (
    <Box className='voucher-timeline'>
      {header || (
        <Typography variant='subtitle1' className='timeline-title'>
          Voucher {voucher.token} lifecycle
        </Typography>
      )}
      <div className='voucher-timeline-events'>
        {lifecycleEvents(voucher).map((event, index) => (
          <div className={`voucher-timeline-event${event.pending ? ' pending' : ''}`} key={`${event.label}-${index}`}>
            <span className='event-dot' />
            <div className='event-body'>
              <span className='event-when' title={timeSourceTitle(event.at, TIME_SOURCE.app)}>{formatDateTime(event.at) || NA}</span>
              <span className='event-text'>
                <span className='event-what'>{event.label}</span>
                {event.detail && <span className='event-detail'> — {event.detail}</span>}
              </span>
            </div>
          </div>
        ))}
      </div>
    </Box>
  )
}

export default VoucherTimeline
